import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, throwError } from 'rxjs';
import {
  ADD_TOAST_NOTIFICATION,
  DOWNLOAD_FILE,
  RETRIEVE_CONFIG,
  RETRIEVE_CONFIG_SUCCESS,
  RETRIVE_CATALOGO_APPARTENENZA,
  RETRIVE_CATALOGO_APPARTENENZA_SUCCESS,
  RETRIVE_CLASSE_APPARTENENZA,
  RETRIVE_CLASSE_APPARTENENZA_SUCCESS,
  RETRIVE_COMUNI,
  RETRIVE_COMUNI_SUCCESS,
  RETRIVE_PROVINCE,
  RETRIVE_PROVINCE_SUCCESS,
  RETRIVE_REGIONI,
  RETRIVE_REGIONI_SUCCESS,
} from './config.actions';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { Config } from '../../interfaces/config';
import { ApiService } from '../../services/api.service';
import { fileXlsxDownload } from '@poste-click-connect/shared-ui';
import {
  IComuni,
  IProvince,
  IRegioni,
  payloadComuni,
  payloadProvince,
} from '../../interfaces/location';
import { CatalogoAppartenenza } from '../../interfaces/catalogo-appartenenza';
import { emptyValueDropdown } from '../../utils/generic';
import { ClasseAppartenenza } from '../../interfaces/classe-appartenenza';
import { INotification } from '../../interfaces/notification';

@Injectable()
export class ConfigEffects {
  retrieveConfig$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RETRIEVE_CONFIG),
      switchMap(() => {
        return this.api.retrieveConfig();
      }),
      mergeMap((response: Config) => {
        return [
          RETRIEVE_CONFIG_SUCCESS({
            config: response,
          }),
        ];
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  addToastNotification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ADD_TOAST_NOTIFICATION),
      switchMap(
        ({
          notificationMessage,
        }: {
          notificationMessage: INotification | null;
        }) => {
          if (notificationMessage?.description) {
            document.querySelector('.container-scroller')?.scroll({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          }

          // Save message already handeled in Reducer
          return of({ type: 'NOOP' });
        }
      ),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  downloadFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DOWNLOAD_FILE),
      switchMap(
        ({
          file,
          fileName,
          blobType,
        }: {
          file: Blob;
          fileName: string;
          blobType?: string;
        }) => {
          fileXlsxDownload(file, fileName, blobType);
          return of({ type: 'NOOP' });
        }
      ),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  retrieveCatalogoAppartenenza$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RETRIVE_CATALOGO_APPARTENENZA),
      switchMap(() => {
        return this.api.retrieveCatalogoAppartenenza();
      }),
      map((catalogoAppartenenza: CatalogoAppartenenza) => {
        return RETRIVE_CATALOGO_APPARTENENZA_SUCCESS({
          catalogoAppartenenza:
            catalogoAppartenenza.catalogoAppartenenzaList.map((item) => ({
              id: item.catalogoAppartenenza,
              label: item.descrizioneMateriale,
            })),
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  retrieveClasseAppartenenza$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RETRIVE_CLASSE_APPARTENENZA),
      switchMap(({ payload }) => {
        return this.api.retrieveClasseAppartenenza(payload);
      }),
      map((classeAppartenenza: ClasseAppartenenza) => {
        return RETRIVE_CLASSE_APPARTENENZA_SUCCESS({
          classeAppartenenza: [
            emptyValueDropdown,
            ...classeAppartenenza.classiAppartenenzaCluster.map((item) => ({
              id: item,
              label: item,
            })),
          ],
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  retriveRegioni$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RETRIVE_REGIONI),
      switchMap(() => {
        return this.api.retriveRegioni();
      }),
      map((regioni: IRegioni) => {
        return RETRIVE_REGIONI_SUCCESS({
          regioni: {
            ...regioni,
            localizzazione: [
              {
                regione: '',
              },
              ...regioni.localizzazione,
            ],
          },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  retriveProvince$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RETRIVE_PROVINCE),
      switchMap(({ payload }: { payload: payloadProvince }) => {
        return this.api.retriveProvince(payload);
      }),
      map((province: IProvince) => {
        return RETRIVE_PROVINCE_SUCCESS({
          province: {
            ...province,
            localizzazione: [
              {
                province: ['', ...province.localizzazione[0].province],
                regione: province.localizzazione[0].regione,
              },
            ],
          },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  retriveComuni$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RETRIVE_COMUNI),
      switchMap(({ payload }: { payload: payloadComuni }) => {
        return this.api.retriveComuni(payload);
      }),
      map((comuni: IComuni) => {
        return RETRIVE_COMUNI_SUCCESS({
          comuni: {
            ...comuni,
            localizzazione: [
              {
                comuni: ['', ...comuni.localizzazione[0].comuni],
                regione: comuni.localizzazione[0].regione,
                siglaProvincia: comuni.localizzazione[0].siglaProvincia,
              },
            ],
          },
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private store: Store
  ) {}
}
